import React, { useState, useEffect, useMemo } from "react";
import { BiPlus } from "react-icons/bi";
import { GrAscend, GrDescend } from "react-icons/gr";
import { PiMagnifyingGlass } from "react-icons/pi";
import OpenModelModal from "./OpenModelModal";
import { TbReload } from "react-icons/tb";
import { VscHubot } from "react-icons/vsc";
import ItemsLoader from "../Loaders/ItemsLoading";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const MainLLmDirectory = ({ account_id }) => {
    const [data, setData] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [show, setShowModal] = useState(false)
    const [selectedModel, setSelectedModel] = useState({})
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");

    const fetchData = async () => {
        setLoadingData(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_models`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    limit: 20
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setData(result.data || []);
        } catch (err) {
            console.error("Error fetching models:", err);
        } finally {
            setLoadingData(false);
        }
    };

    const filteredAndSortedData = useMemo(() => {
        if (!data) return [];

        const term = searchTerm.toLowerCase();

        let result = data.filter((model) => {
            if (!term) return true;

            const searchInObject = (obj) => {
                return Object.values(obj).some((value) => {
                    if (value === null || value === undefined) return false;

                    if (typeof value === "string" || typeof value === "number") {
                        return String(value).toLowerCase().includes(term);
                    }

                    if (typeof value === "boolean") {
                        return false;
                    }

                    if (typeof value === "object") {
                        return searchInObject(value);
                    }

                    return false;
                });
            };

            return searchInObject(model);
        });

        result.sort((a, b) => {
            const sortKey = "name";

            let valA = a[sortKey];
            let valB = b[sortKey];

            if (valA === null || valA === undefined) valA = "";
            if (valB === null || valB === undefined) valB = "";

            if (typeof valA === "number" && typeof valB === "number") {
                return sortOrder === "asc" ? valA - valB : valB - valA;
            }

            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();

            return sortOrder === "asc"
                ? strA.localeCompare(strB)
                : strB.localeCompare(strA);
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    useEffect(() => {
        fetchData();
    }, []);

    if (loadingData) {
        return <ItemsLoader />
    }

    return (
        <div className="mainframe_item">
            <div className="mainframe_item_header_directory">
                <p className="mainframe_item_title fade-in">LLM Directory</p>
                <div className="mainframe_item_subtitle fade-in">
                    {data.length == 0 ? (
                        <div className="empty_org_mex_cont">
                            <p>Configure LLMs and scale them across your organizations</p>
                            <div className="empty_org_img">
                                <p className="empty_org_img_title">
                                    <i><VscHubot /></i> Understanding LLMs
                                </p>
                                <p className="empty_org_img_subtitle">
                                    Large Language Models (LLMs) are the engines that power your AI applications.
                                    Connect a model provider to start generating content, analyzing data,
                                    and automating your workflows.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p>Monitor and manage your connected LLMs and their configurations.</p>
                    )}
                </div>
            </div>

            <div className="filter_body_mainframe">
                <div className="filtering_mainframe">
                    <div className="filtering_mainframe_btn_cont fade-in">
                        {data.length > 1 && (
                            <>
                                <button
                                    className={`filter_btn ${sortOrder === "asc" ? "active" : ""}`}
                                    onClick={() => setSortOrder("asc")}
                                >
                                    <GrAscend />
                                </button>
                                <button
                                    className={`filter_btn ${sortOrder === "desc" ? "active" : ""}`}
                                    onClick={() => setSortOrder("desc")}
                                    title="Sort Z-A"
                                >
                                    <GrDescend />
                                </button>
                            </>
                        )}
                        <button
                            className="filter_btn"
                            onClick={() => fetchData()}
                            title="Reload Data"
                        >
                            <TbReload />
                        </button>
                    </div>
                    <div className="filter_input_cont fade-in">
                        <input
                            className="filter_input"
                            placeholder="Search by name, provider and more..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                    </div>
                </div>

                {data.length > 0 && (
                    <div className="organizations_cont">
                        {filteredAndSortedData.length === 0 ? (
                            <div className="organizations_cont_no_items_mex fade-in">
                                <p>No LLMs found matching your search.</p>
                            </div>
                        ) : (
                            <div className="organizations_cont_inner">
                                {filteredAndSortedData.map((model) => (
                                    <div
                                        className="model_item"
                                        key={model.id}
                                        onClick={() => {
                                            setSelectedModel(model);
                                            setShowModal(true);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <div className="model_name">
                                            {model.name}
                                            {model.version && ` v${model.version}`}
                                        </div>

                                        <div className="model_creator">
                                            {model.creator_name} • {model.inference_provider}
                                        </div>

                                        <div className="model_tags">
                                            {model.supports_text && <span>Text</span>}
                                            {model.supports_vision && <span>Vision</span>}
                                            {model.supports_audio && <span>Audio</span>}
                                            {model.supports_video && <span>Video</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>

                        )}
                    </div>
                )}
            </div>
            <OpenModelModal account_id={account_id} show={show} handleClose={() => setShowModal(false)} fetchRecords={fetchData} selectedModel={selectedModel} />
        </div>
    );
};

export default MainLLmDirectory;